import { Link } from 'react-router';
import { ChevronRight, Trash2 } from '@/components/icons';
import type { Run } from '@/lib/types';
import { formatDuration, isRunDeletable, projectNameOf, runCounts, runDuration, runShortId, runTitle, timeAgo } from '@/lib/format';
import { StatusPill } from '@/components/ui';

/**
 * Compact, responsive list of runs linking to the report page.
 * `canDelete` + `onDelete` add a delete button to finished runs the user may delete;
 * `selected` + `onToggleSelect` add a checkbox to those runs for deleting several at once.
 */
export function RunsTable({
  runs,
  showProject = true,
  canDelete,
  onDelete,
  selected,
  onToggleSelect,
}: {
  runs: Run[];
  showProject?: boolean;
  canDelete?: (run: Run) => boolean;
  onDelete?: (run: Run) => void;
  selected?: ReadonlySet<string>;
  onToggleSelect?: (run: Run) => void;
}) {
  const selectable = Boolean(selected && onToggleSelect);
  const runLabel = (run: Run) => `${runTitle(run)} (run ${runShortId(run)})`;
  return (
    <ul className="divide-y divide-border-subtle">
      {runs.map((run) => {
        const c = runCounts(run);
        const pct = c.total ? (c.passed / c.total) * 100 : 0;
        const deletable = isRunDeletable(run, canDelete);
        return (
          <li key={run.id} className="flex items-center">
            {selectable && (
              <span className="flex w-10 shrink-0 justify-end">
                {deletable && (
                  <input
                    type="checkbox"
                    checked={selected!.has(run.id)}
                    onChange={() => onToggleSelect!(run)}
                    aria-label={`Select report for ${runLabel(run)}`}
                    className="size-4.5 rounded-bar border-border accent-primary"
                  />
                )}
              </span>
            )}
            <Link
              to={`/runs/${run.id}`}
              className="group flex min-w-0 flex-1 items-center gap-4 px-5 py-3.5 transition hover:bg-surface-sunken"
            >
              <StatusPill status={run.status} />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="truncate text-sm font-medium text-fg" title={run.testNames?.join('\n')}>
                    {runTitle(run)}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-fg-muted" title={`Run ID: ${run.id}`}>
                    {runShortId(run)}
                  </span>
                  {run.collectionName && (
                    <span className="shrink-0 text-xs font-medium text-link" title="Test data collection">
                      {run.collectionName}
                    </span>
                  )}
                  {run.appBuildLabel && (
                    <span className="shrink-0 text-xs text-fg-secondary" title="App build">
                      Build {run.appBuildLabel}
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-fg-muted">
                  {showProject ? `${projectNameOf(run)} · ` : ''}
                  {timeAgo(run.queuedAt)}
                  {run.triggeredBy?.name ? ` · by ${run.triggeredBy.name}` : ''}
                </div>
              </div>
              <div className="hidden w-40 sm:block" aria-label={`${c.passed} of ${c.total} passed`}>
                <div className="mb-1 flex justify-between text-xs text-fg-muted tabular-nums">
                  <span>
                    {c.passed}/{c.total} passed
                  </span>
                  <span>{formatDuration(runDuration(run))}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-bar bg-surface-raised">
                  <div className="h-full rounded-bar bg-success transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <ChevronRight className="size-3 text-fg-muted transition group-hover:translate-x-0.5 group-hover:text-fg" />
            </Link>
            {onDelete && deletable && (
              <button
                type="button"
                onClick={() => onDelete(run)}
                aria-label={`Delete report for ${runLabel(run)}`}
                title="Delete report"
                className="mr-3 grid place-items-center rounded-full text-fg ring-1 ring-border ring-inset transition size-8 hover:bg-danger-subtle hover:text-danger-text hover:ring-danger-line focus-visible:ring-2 focus-visible:ring-danger-text focus-visible:outline-none"
              >
                <Trash2 className="size-4" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
